<template>
  <Portal>
    <Modal
      data-testid="receipt-modal"
      class="receipt-modal"
      size="md"
      title=""
      :is-open="isOpen"
      @close="close"
    >
      <div v-if="showCancelContent">
        <HelperViewContextual
          class="receipt-modal__cancel-content"
          :title="helperViewMessage.title"
          :description="helperViewMessage.description"
          :icon="helperViewMessage.icon"
          status="alert"
        />
      </div>
      <div v-else class="receipt-modal__content">
        <HeadingDisplay
          class="receipt-modal__header"
          size="small"
          :head="transaction?.module"
          :title="transaction?.name"
          :subtitle="transaction?.fullDate"
        />
        <ContentDisplay class="receipt-modal__list" :list-length="1">
          <template #content-block-1>
            <ListDisplay
              v-for="(item, key) in transaction?.details"
              :key="key"
              :value-as-bold="item.bold"
              :title="item.label"
              :value="item.value"
            />
          </template>
        </ContentDisplay>
      </div>
      <template v-if="showScheduledButton" #buttonContainer>
        <div v-if="showCancelContent" class="receipt-modal__cancel">
          <ButtonPrimary
            v-if="!hasError"
            :loading="isLoading"
            data-testid="cancel-schedule-button-confirm"
            label="Cancelar agendamento"
            @click="cancelSchedule"
          />
          <ButtonTertiary label="Voltar" @click="backDescriptionModal" />
        </div>
        <div v-else class="receipt-modal__cancel">
          <ButtonTertiary
            label="Cancelar agendamento"
            data-testid="cancel-schedule-button"
            @click="confirmCancelScheduled"
          />
        </div>
      </template>
    </Modal>
  </Portal>
</template>

<script lang="ts">
import { Component, Vue, Prop, Emit } from 'vue-property-decorator';

import {
  Modal,
  HeadingDisplay,
  ContentDisplay,
  ListDisplay,
  ButtonTertiary,
  HelperViewContextual,
  ButtonPrimary
} from '@warrenbrasil/nebraska-web';
import { Portal } from '@/modules/common/components/Portal';

import {
  IVoucherRequest,
  IStatementTransaction
} from '@/modules/transaction/services/unified-account';
import { deleteScheduledTransaction } from '@/modules/transaction/services/banking';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/wallet-builder/src/types';

@Component({
  components: {
    Modal,
    HeadingDisplay,
    ContentDisplay,
    ListDisplay,
    ButtonPrimary,
    ButtonTertiary,
    Portal,
    HelperViewContextual
  }
})
export default class ReceiptModal extends Vue {
  @Prop({ type: Boolean, default: false })
  readonly isOpen!: boolean;

  @Prop({ type: Object, required: false, default: () => ({}) })
  readonly transaction!: IStatementTransaction;

  @Prop({ type: Object, required: false })
  readonly filters?: IVoucherRequest;

  @Prop({ type: Boolean, default: null })
  readonly isUpcomingTransfer?: boolean;

  @Emit('close')
  close() {
    this.showCancelContent = false;
    this.isLoading = false;
    this.hasError = false;
  }

  @Emit('schedule')
  schedule() {}

  readonly helperViewContent = {
    default: {
      title: 'Cancelar agendamento?',
      description:
        'Seu agendamento será cancelado e o valor vai permanecer na sua conta.',
      icon: 'EF0060'
    },
    error: {
      title: 'Houve um erro ao cancelar seu agendamento',
      description:
        'Não foi possível cancelar seu agendamento. Tente novamente em alguns minutos.',
      icon: 'EF0062'
    }
  };

  isLoading = false;
  hasError = false;
  showCancelContent = false;

  get helperViewMessage() {
    return this.hasError
      ? this.helperViewContent.error
      : this.helperViewContent.default;
  }

  get showScheduledButton() {
    return (
      this.transaction?.canCancel &&
      this.transaction?.direction.includes('Future')
    );
  }

  async cancelSchedule() {
    this.isLoading = true;
    try {
      await deleteScheduledTransaction(this.transaction.id);
      notify({
        group: 'wrn-notifications',
        text: 'Seu agendamento foi cancelado.',
        type: NotificationTypes.success
      });
      this.schedule();
      this.close();
    } catch {
      this.hasError = true;
    } finally {
      this.isLoading = false;
    }
  }

  backDescriptionModal() {
    this.showCancelContent = false;
    this.hasError = false;
  }

  confirmCancelScheduled() {
    this.showCancelContent = true;
  }
}
</script>

<style lang="less" scoped>
.receipt-modal {
  /deep/.base-modal--wrapper {
    max-width: 592px;
  }
  &__content {
    max-width: 100%;
  }
  &__header {
    text-align: left;
    margin-bottom: @size-spacing-x600;
  }
  &__list {
    text-align: left;
    /deep/.list-display__text {
      max-width: 320px;
    }
  }
  &__cancel {
    border-top: @size-border-x400 solid @divider-primary;
    display: flex;
    justify-content: center;
    padding: @size-spacing-x400;
    gap: @size-spacing-x400;

    &-content {
      :deep(.initial-avatar-icon) {
        background: @status-alert-background;
        color: @status-alert;
      }
    }
  }
}
</style>
